/**
 * Vue Composable for PostHog Event Tracking
 * Australian Telecommunications Platform
 * 
 * Provides reactive PostHog tracking hooks that can be used in any Vue component
 * with built-in lifecycle management and error handling.
 */

import { onMounted, onUnmounted, ref, computed } from 'vue';
import {
  trackEvent,
  trackProductEvent,
  trackCheckoutEvent,
  trackActivationEvent,
  trackDashboardEvent,
  trackVerificationEvent,
  trackError,
  trackPerformance,
  EVENT_CATEGORIES,
  EVENT_ACTIONS
} from '~/utils/posthogTracking';

export interface TrackingTimers {
  [key: string]: number;
}

export interface PerformanceMetrics {
  [key: string]: {
    startTime: number;
    endTime?: number;
    duration?: number;
  };
}

/**
 * Main PostHog tracking composable
 */
export const usePostHogTracking = (componentName: string = 'UnknownComponent') => {
  // Reactive state for timing and performance tracking
  const timers = ref<TrackingTimers>({});
  const performanceMetrics = ref<PerformanceMetrics>({});
  
  // Component lifecycle tracking
  const componentMountTime = ref<number>(0);
  
  // Track component mount/unmount
  onMounted(() => {
    componentMountTime.value = Date.now();
    
    // Track component viewed
    trackEvent('component_viewed', {
      mount_time: componentMountTime.value,
    }, componentName);
  });
  
  onUnmounted(() => {
    const sessionDuration = Date.now() - componentMountTime.value;
    
    // Track component session
    trackEvent('component_session_ended', {
      session_duration_ms: sessionDuration,
    }, componentName);
  });
  
  /**
   * Generic event tracking with component context
   */
  const track = (eventName: string, properties: Record<string, any> = {}) => {
    trackEvent(eventName, properties, componentName);
  };
  
  /**
   * Timer management for measuring user actions
   */
  const startTimer = (actionName: string): void => {
    timers.value[actionName] = Date.now();
  };
  
  const endTimer = (actionName: string, trackPerformanceEvent: boolean = true): number => {
    const startTime = timers.value[actionName];
    if (!startTime) {
      console.warn(`[PostHog] Timer '${actionName}' was not started`);
      return 0;
    }
    
    const duration = Date.now() - startTime;
    delete timers.value[actionName];
    
    if (trackPerformanceEvent) {
      trackPerformance(actionName, duration, componentName);
    }
    
    return duration;
  };
  
  /**
   * Performance measurement wrapper
   */
  const measurePerformance = async <T>(
    actionName: string,
    asyncAction: () => Promise<T>,
    trackEvent: boolean = true
  ): Promise<T> => {
    const startTime = Date.now();
    
    try {
      const result = await asyncAction();
      const duration = Date.now() - startTime;
      
      if (trackEvent) {
        trackPerformance(actionName, duration, componentName, {
          success: true,
        });
      }
      
      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      if (trackEvent) {
        trackPerformance(actionName, duration, componentName, {
          success: false,
          error_message: error instanceof Error ? error.message : 'Unknown error',
        });
      }
      
      throw error;
    }
  };
  
  /**
   * Error tracking with component context
   */
  const trackComponentError = (
    errorType: string,
    error: Error | string,
    additionalContext: Record<string, any> = {}
  ) => {
    const errorMessage = error instanceof Error ? error.message : error;
    trackError(errorType, errorMessage, componentName, additionalContext);
  };
  
  /**
   * Form interaction tracking
   */
  const trackFormEvent = (
    action: 'started' | 'field_focused' | 'field_completed' | 'validation_failed' | 'submitted' | 'abandoned',
    formName: string,
    additionalData: Record<string, any> = {}
  ) => {
    track(`form_${action}`, {
      form_name: formName,
      ...additionalData,
    });
  };
  
  /**
   * Navigation tracking
   */
  const trackNavigation = (
    fromPath: string,
    toPath: string,
    method: 'click' | 'programmatic' | 'browser' = 'click'
  ) => {
    track('navigation_performed', {
      from_path: fromPath,
      to_path: toPath,
      navigation_method: method,
    });
  };
  
  /**
   * Feature usage tracking
   */
  const trackFeatureUsage = (
    featureName: string,
    action: string,
    context: Record<string, any> = {}
  ) => {
    track('feature_used', {
      feature_name: featureName,
      feature_action: action,
      ...context,
    });
  };
  
  // Return all tracking functions and utilities
  return {
    // Core tracking
    track,
    trackComponentError,
    
    // Specialized tracking helpers
    product: trackProductEvent,
    checkout: trackCheckoutEvent,
    activation: trackActivationEvent,
    dashboard: trackDashboardEvent,
    verification: trackVerificationEvent,
    
    // Form and interaction tracking
    trackFormEvent,
    trackNavigation,
    trackFeatureUsage,
    
    // Performance and timing
    startTimer,
    endTimer,
    measurePerformance,
    
    // State
    timers: computed(() => timers.value),
    performanceMetrics: computed(() => performanceMetrics.value),
    componentMountTime: computed(() => componentMountTime.value),
    
    // Constants for consistency
    EVENT_CATEGORIES,
    EVENT_ACTIONS,
  };
};

/**
 * Specialized composables for specific components
 */

/**
 * Product page tracking composable
 */
export const useProductTracking = (componentName: string = 'ProductPage') => {
  const { track, startTimer, endTimer, trackComponentError, product } = usePostHogTracking(componentName);
  
  const trackPageLoad = (productId: number, loadTime: number) => {
    track('product_page_loaded', {
      product_id: productId,
      load_time_ms: loadTime,
    });
  };
  
  const trackProductInteraction = (
    action: 'zoom' | 'gallery_navigate' | 'spec_expand' | 'share',
    productId: number,
    context: Record<string, any> = {}
  ) => {
    track('product_interaction', {
      interaction_type: action,
      product_id: productId,
      ...context,
    });
  };
  
  return {
    track,
    trackComponentError,
    trackPageLoad,
    trackProductInteraction,
    startTimer,
    endTimer,
    ...product,
  };
};

/**
 * Checkout flow tracking composable
 */
export const useCheckoutTracking = (componentName: string = 'CheckoutFlow') => {
  const { track, startTimer, endTimer, trackComponentError, checkout, measurePerformance } = usePostHogTracking(componentName);
  
  const trackStepStarted = (stepIndex: number, stepName: string) => {
    startTimer(`step_${stepIndex}`);
    track('checkout_step_started', {
      step_number: stepIndex + 1,
      step_name: stepName,
    });
  };
  
  const trackStepCompleted = (stepIndex: number, stepName: string, validated: boolean = true) => {
    const duration = endTimer(`step_${stepIndex}`, false);
    checkout.stepCompleted(stepIndex, stepName, validated, duration);
  };
  
  const trackValidationError = (stepIndex: number, field: string, errorMessage: string) => {
    track('checkout_validation_error', {
      step_number: stepIndex + 1,
      field_name: field,
      error_message: errorMessage,
    });
  };
  
  return {
    track,
    trackComponentError,
    trackStepStarted,
    trackStepCompleted,
    trackValidationError,
    measurePerformance,
    ...checkout,
  };
};

/**
 * Dashboard tracking composable
 */
export const useDashboardTracking = (componentName: string = 'Dashboard') => {
  const { track, trackFeatureUsage, dashboard } = usePostHogTracking(componentName);
  
  const trackServiceAction = (
    action: 'view_details' | 'edit_plan' | 'toggle_auto_recharge' | 'add_credit' | 'view_usage',
    serviceId: string,
    serviceType: string,
    context: Record<string, any> = {}
  ) => {
    trackFeatureUsage('service_management', action, {
      service_id: serviceId,
      service_type: serviceType,
      ...context,
    });
  };
  
  return {
    track,
    trackServiceAction,
    ...dashboard,
  };
};

/**
 * Form tracking composable with validation events
 */
export const useFormTracking = (formName: string, componentName?: string) => {
  const { trackFormEvent, startTimer, endTimer } = usePostHogTracking(componentName || `${formName}Form`);
  
  const trackFieldFocus = (fieldName: string) => {
    startTimer(`field_${fieldName}`);
    trackFormEvent('field_focused', formName, { field_name: fieldName });
  };
  
  const trackFieldBlur = (fieldName: string, hasValue: boolean, isValid: boolean) => {
    const duration = endTimer(`field_${fieldName}`, false);
    trackFormEvent('field_completed', formName, {
      field_name: fieldName,
      has_value: hasValue,
      is_valid: isValid,
      field_time_ms: duration,
    });
  };
  
  const trackValidationError = (fieldName: string, errorMessage: string) => {
    trackFormEvent('validation_failed', formName, {
      field_name: fieldName,
      error_message: errorMessage,
    });
  };
  
  const trackFormSubmission = (success: boolean, errorMessage?: string) => {
    const eventType = success ? 'submitted' : 'validation_failed';
    trackFormEvent(eventType, formName, {
      success,
      error_message: errorMessage,
    });
  };
  
  return {
    trackFieldFocus,
    trackFieldBlur,
    trackValidationError,
    trackFormSubmission,
    trackFormStarted: () => trackFormEvent('started', formName),
    trackFormAbandoned: () => trackFormEvent('abandoned', formName),
  };
};
